/**
 * Gradient System Toggle
 * 
 * Provides runtime control for switching between solid colors and gradients
 * without requiring theme recompilation.
 */

(function() {
  'use strict';
  
  const GradientSystem = {
    
    // Section flags
    sections: {
      body: true,
      header: true,
      footer: true,
      buttons: true,
      drawers: true,
      cards: true
    },
    
    // Check if gradients are enabled from theme settings
    isEnabled: function() {
      const rootStyle = getComputedStyle(document.documentElement);
      const useGradients = rootStyle.getPropertyValue('--use-gradients').trim();
      return useGradients === '1';
    },
    
    // Get section-specific settings from theme
    getSectionSettings: function() {
      const rootStyle = getComputedStyle(document.documentElement);
      return {
        body: rootStyle.getPropertyValue('--gradients-body').trim() === '1',
        header: rootStyle.getPropertyValue('--gradients-header').trim() === '1',
        footer: rootStyle.getPropertyValue('--gradients-footer').trim() === '1',
        buttons: rootStyle.getPropertyValue('--gradients-buttons').trim() === '1',
        drawers: rootStyle.getPropertyValue('--gradients-drawers').trim() === '1',
        cards: rootStyle.getPropertyValue('--gradients-cards').trim() === '1'
      };
    },
    
    // Get border-specific settings from theme
    getBorderSettings: function() {
      const rootStyle = getComputedStyle(document.documentElement);
      return {
        sections: rootStyle.getPropertyValue('--gradient-borders-sections').trim() === '1',
        footer: rootStyle.getPropertyValue('--gradient-borders-footer').trim() === '1',
        collections: rootStyle.getPropertyValue('--gradient-borders-collections').trim() === '1',
        tables: rootStyle.getPropertyValue('--gradient-borders-tables').trim() === '1',
        cards: rootStyle.getPropertyValue('--gradient-borders-cards').trim() === '1'
      };
    },
    
    // Initialize gradient mode based on settings
    init: function() {
      // Check if dev mode is enabled
      const rootStyle = getComputedStyle(document.documentElement);
      const devMode = rootStyle.getPropertyValue('--gradient-dev-mode').trim() === '1';
      
      // Always prioritize theme settings, only use localStorage in dev mode
      if (devMode) {
        console.log('🔧 Gradient Dev Mode: ENABLED - localStorage overrides active');
        const savedSections = localStorage.getItem('gradient-sections');
        if (savedSections) {
          this.sections = JSON.parse(savedSections);
        } else {
          this.sections = this.getSectionSettings();
        }
      } else {
        // Production mode: always use theme settings, ignore localStorage
        console.log('✅ Gradient Production Mode: Using theme settings only');
        this.sections = this.getSectionSettings();
        // Clear any localStorage overrides
        localStorage.removeItem('gradient-sections');
        localStorage.removeItem('gradient-mode');
      }
      
      // Initialize border settings from theme settings
      const borderSettings = this.getBorderSettings();
      Object.keys(borderSettings).forEach(section => {
        // Only set if not already in localStorage AND dev mode is on
        if (devMode && localStorage.getItem(`gradient_borders_${section}`) === null) {
          localStorage.setItem(`gradient_borders_${section}`, borderSettings[section]);
        } else if (!devMode) {
          // In production, always use theme settings
          localStorage.removeItem(`gradient_borders_${section}`);
        }
      });
      
      if (this.isEnabled()) {
        this.enable();
      }
      
      // Listen for storage events (for syncing across tabs) - only in dev mode
      if (devMode) {
        window.addEventListener('storage', function(e) {
          if (e.key === 'gradient-mode') {
            if (e.newValue === 'true') {
              GradientSystem.enable();
            } else {
              GradientSystem.disable();
            }
          } else if (e.key === 'gradient-sections') {
            GradientSystem.sections = JSON.parse(e.newValue);
            GradientSystem.updateSections();
          }
        });
      }
      
      // Check for saved preference - DISABLED for production
      // const savedPreference = localStorage.getItem('gradient-mode');
      // if (savedPreference === 'true') {
      //   this.enable();
      // } else if (savedPreference === 'false') {
      //   this.disable();
      // }
      
      // Apply section settings
      this.updateSections();
    },
    
    // Update data attributes for section-specific gradients
    updateSections: function() {
      const body = document.body;
      body.setAttribute('data-gradient-body', this.sections.body);
      body.setAttribute('data-gradient-header', this.sections.header);
      body.setAttribute('data-gradient-footer', this.sections.footer);
      body.setAttribute('data-gradient-buttons', this.sections.buttons);
      body.setAttribute('data-gradient-drawers', this.sections.drawers);
      body.setAttribute('data-gradient-cards', this.sections.cards);
      
      // Button enhancement toggles
      const rootStyle = getComputedStyle(document.documentElement);
      const btnBorderEnabled = rootStyle.getPropertyValue('--gradient-btn-border-enabled').trim() === '1';
      const btnShadowEnabled = rootStyle.getPropertyValue('--gradient-btn-shadow-enabled').trim() === '1';
      const btnBeforeEnabled = rootStyle.getPropertyValue('--gradient-btn-before-enabled').trim() === '1';
      body.setAttribute('data-gradient-btn-border', btnBorderEnabled);
      body.setAttribute('data-gradient-btn-shadow', btnShadowEnabled);
      body.setAttribute('data-gradient-btn-before', btnBeforeEnabled);
      
      // Inject gradient shadow elements into buttons if shadow is enabled
      if (btnShadowEnabled) {
        this.injectButtonShadows();
      }
      
      // Border toggles - read from localStorage or theme settings
      const borderSections = ['sections', 'footer', 'collections', 'tables', 'cards'];
      borderSections.forEach(section => {
        const storedValue = localStorage.getItem(`gradient_borders_${section}`);
        const enabled = storedValue === 'true' || storedValue === true;
        body.setAttribute(`data-gradient-borders-${section}`, enabled);
        console.log(`🎨 Gradient border [${section}]: ${enabled} (value: ${storedValue})`);
      });
      
      // Save to localStorage
      localStorage.setItem('gradient-sections', JSON.stringify(this.sections));
    },
    
    // Enable gradient mode
    enable: function() {
      document.body.setAttribute('data-gradients', 'true');
      document.body.classList.add('gradient-mode');
      localStorage.setItem('gradient-mode', 'true');
      this.updateSections();
      
      // Dispatch custom event for other scripts
      window.dispatchEvent(new CustomEvent('gradientModeChanged', { 
        detail: { enabled: true, sections: this.sections } 
      }));
      
      console.log('✨ Gradient mode enabled');
    },
    
    // Disable gradient mode
    disable: function() {
      document.body.removeAttribute('data-gradients');
      document.body.classList.remove('gradient-mode');
      localStorage.setItem('gradient-mode', 'false');
      
      // Dispatch custom event for other scripts
      window.dispatchEvent(new CustomEvent('gradientModeChanged', { 
        detail: { enabled: false } 
      }));
      
      console.log('🎨 Solid color mode enabled');
    },
    
    // Toggle between modes
    toggle: function() {
      if (document.body.classList.contains('gradient-mode')) {
        this.disable();
      } else {
        this.enable();
      }
    },
    
    // Enable/disable specific section
    toggleSection: function(section, enabled) {
      if (this.sections.hasOwnProperty(section)) {
        this.sections[section] = enabled !== undefined ? enabled : !this.sections[section];
        this.updateSections();
        console.log(`${section} gradients: ${this.sections[section] ? 'enabled' : 'disabled'}`);
      }
    },
    
    // Update a specific gradient
    setGradient: function(name, startColor, endColor, angle = '135deg') {
      const gradient = `linear-gradient(${angle}, ${startColor} 0%, ${endColor} 100%)`;
      document.documentElement.style.setProperty(`--gradient-${name}`, gradient);
      console.log(`Updated gradient: ${name}`);
    },
    
    // Create a custom gradient utility
    createGradient: function(startColor, endColor, angle = '135deg') {
      return `linear-gradient(${angle}, ${startColor} 0%, ${endColor} 100%)`;
    },
    
    // Inject gradient shadow <span> elements into applicable buttons
    injectButtonShadows: function() {
      const btnSelectors = [
        '.btn--primary', '.btn-primary',
        '.btn--secondary', '.btn-secondary',
        '.btn-outline-primary', '.btn-outline-secondary'
      ];
      
      btnSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(btn => {
          // Don't inject twice
          if (btn.querySelector('.gradient-btn-shadow')) return;
          
          const shadow = document.createElement('span');
          shadow.className = 'gradient-btn-shadow';
          shadow.setAttribute('aria-hidden', 'true');
          btn.insertBefore(shadow, btn.firstChild);
        });
      });
      
      console.log('✨ Gradient button shadows injected');
    }
  };
  
  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      GradientSystem.init();
    });
  } else {
    GradientSystem.init();
  }
  
  // Expose to window for manual control
  window.GradientSystem = GradientSystem;
  
  // Add keyboard shortcut (Ctrl+Shift+G) for quick testing - DISABLED for production
  // document.addEventListener('keydown', function(e) {
  //   if (e.ctrlKey && e.shiftKey) {
  //     switch(e.key) {
  //       case 'G':
  //         e.preventDefault();
  //         GradientSystem.toggle();
  //         break;
  //       case 'B':
  //         e.preventDefault();
  //         GradientSystem.toggleSection('body');
  //         break;
  //       case 'H':
  //         e.preventDefault();
  //         GradientSystem.toggleSection('header');
  //         break;
  //       case 'F':
  //         e.preventDefault();
  //         GradientSystem.toggleSection('footer');
  //         break;
  //     }
  //   }
  // });
  
})();

/**
 * USAGE EXAMPLES:
 * 
 * // Enable gradients manually
 * GradientSystem.enable();
 * 
 * // Disable gradients
 * GradientSystem.disable();
 * 
 * // Toggle gradients
 * GradientSystem.toggle();
 * 
 * // Enable/disable specific sections
 * GradientSystem.toggleSection('body', true);  // Enable body gradients
 * GradientSystem.toggleSection('header', false);  // Disable header gradients
 * GradientSystem.toggleSection('buttons');  // Toggle button gradients
 * 
 * // Enable only specific sections
 * GradientSystem.sections.body = true;
 * GradientSystem.sections.header = false;
 * GradientSystem.sections.footer = true;
 * GradientSystem.sections.buttons = true;
 * GradientSystem.sections.drawers = false;
 * GradientSystem.sections.cards = true;
 * GradientSystem.updateSections();
 * 
 * // Update a specific gradient dynamically
 * GradientSystem.setGradient('primary', '#ff0000', '#ff6600', '90deg');
 * 
 * // Create a custom gradient
 * const myGradient = GradientSystem.createGradient('#000', '#fff', '45deg');
 * 
 * // Listen for gradient mode changes
 * window.addEventListener('gradientModeChanged', function(e) {
 *   console.log('Gradient mode:', e.detail.enabled);
 *   console.log('Active sections:', e.detail.sections);
 * });
 * 
 * // Quick toggle with keyboard: Ctrl+Shift+G
 * 
 * // Section-specific keyboard shortcuts:
 * // Ctrl+Shift+B - Toggle body gradients
 * // Ctrl+Shift+H - Toggle header gradients
 * // Ctrl+Shift+F - Toggle footer gradients
 */
